/**
 * One server you are signed in to: who you are there, its teams (each opens into a
 * {@link TeamPanel}), making a team, joining one with an invite link, signing out.
 */
import { For, Show, createSignal, type JSX } from "solid-js";
import { parseInviteLink } from "../../client/space";
import { useApp } from "../../app/context";
import { toast } from "../../shared/toast";
import { TeamPanel } from "./TeamPanel";

/** The section of `server`. */
export function ServerSection(props: { server: string }): JSX.Element {
  const { accounts } = useApp();
  const [open, setOpen] = createSignal<string | null>(null);
  const [teamName, setTeamName] = createSignal("");
  const [invite, setInvite] = createSignal("");
  const me = () => accounts.me(props.server);
  const host = () => new URL(props.server).host;

  /** Opens a team (loading it), or closes it. */
  const toggle = (id: string) => {
    if (open() === id) return setOpen(null);
    setOpen(id);
    void accounts.loadTeam(props.server, id);
  };
  /** Makes the team named in the field, and opens it. */
  const createTeam = async () => {
    const team = await accounts.createTeam(props.server, teamName());
    if (team) {
      setTeamName("");
      toggle(team.id);
    }
  };
  /** Offers the pasted invite link (it may be another server's: signing in there comes next). */
  const join = () => {
    const link = parseInviteLink(invite());
    if (!link) return toast("That is not an invite link: it looks like http://server:7878/join/<code>", "error");
    setInvite("");
    void accounts.offer(link);
  };

  return (
    <section class="grp home-server" data-session={props.server}>
      <h3>
        {host()}
        <Show when={me()}>{(m) => <span class="muted small"> · {m().account.name} ({m().account.email})</span>}</Show>
      </h3>
      <ul class="share-list">
        <For each={me()?.teams ?? []} fallback={<li class="muted small">No team yet: make one, or join one with an invite link.</li>}>
          {(team) => (
            <li data-team={team.id} data-team-role={team.role} class="home-team">
              <button type="button" class="linklike" data-team-open onClick={() => toggle(team.id)}>
                <b>{team.name}</b>
              </button>
              <span class="muted small">{team.role}</span>
              <Show when={open() === team.id}>
                <TeamPanel server={props.server} id={team.id} />
              </Show>
            </li>
          )}
        </For>
      </ul>
      <form
        class="row-inline"
        onSubmit={(e) => {
          e.preventDefault();
          void createTeam();
        }}
      >
        <input class="field" data-new-team-name placeholder="New team name" value={teamName()} onInput={(e) => setTeamName(e.currentTarget.value)} />
        <button type="submit" class="btn" data-new-team>
          New team
        </button>
      </form>
      <form
        class="row-inline"
        onSubmit={(e) => {
          e.preventDefault();
          join();
        }}
      >
        <input class="field" data-join-link placeholder="Invite link: http://server/join/…" value={invite()} onInput={(e) => setInvite(e.currentTarget.value)} />
        <button type="submit" class="btn" data-join>
          Join
        </button>
      </form>
      <span class="btnrow">
        <button type="button" class="mini" data-sign-out onClick={() => void accounts.signOut(props.server)}>
          Sign out
        </button>
      </span>
    </section>
  );
}
